"use client";
import Image from "next/image";

import { useState } from "react";
import Link from "next/link";
import CalculatorShell from "@/components/CalculatorShell";
import CitationSection from "@/components/CitationSection";
import InputField from "@/components/InputField";
import SelectField from "@/components/SelectField";
import CheckboxField from "@/components/CheckboxField";
import ResultCard from "@/components/ResultCard";
import FaqSection from "@/components/FaqSection";
import RelatedCalculators from "@/components/RelatedCalculators";
import Icon from "@/components/Icon";
import { calcSolar, type SolarResult, SOLAR_AVG_ELECTRICITY_RATE_USD_KWH, SOLAR_AVG_COST_PER_WATT_INSTALLED, SOLAR_FEDERAL_ITC_PCT } from "@/lib/formulas";
import { CALCULATOR_SPECS } from "@/lib/calculator-info-data";

const SUN_HOURS_OPTIONS = [
  { value: 3.5, label: "3.5 h/day — Pacific NW / Alaska" },
  { value: 4.0, label: "4.0 h/day — Midwest / Great Plains" },
  { value: 4.5, label: "4.5 h/day — Mid-Atlantic / Southeast" },
  { value: 5.0, label: "5.0 h/day — Texas / Arizona (avg)" },
  { value: 5.5, label: "5.5 h/day — Florida / Southwest" },
  { value: 6.0, label: "6.0 h/day — Desert Southwest (peak)" },
];
const PANEL_WATT_OPTIONS = [
  { value: 350, label: "350 W — Entry level" },
  { value: 400, label: "400 W — Standard (most common)" },
  { value: 450, label: "450 W — High efficiency" },
  { value: 500, label: "500 W — Premium / commercial" },
];
const COVERAGE_OPTIONS = [
  { value: 80, label: "80% offset (common, cost-effective)" },
  { value: 100, label: "100% offset (go net-zero)" },
  { value: 60, label: "60% offset (budget-friendly)" },
  { value: 50, label: "50% offset (minimal / starter)" },
];

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function CalculatorClient() {
  const [monthlyKwh, setMonthlyKwh] = useState("900");
  const [peakSunHours, setPeakSunHours] = useState(4.5);
  const [panelWatts, setPanelWatts] = useState(400);
  const [electricityRate, setElectricityRate] = useState(String(SOLAR_AVG_ELECTRICITY_RATE_USD_KWH));
  const [costPerWatt, setCostPerWatt] = useState(String(SOLAR_AVG_COST_PER_WATT_INSTALLED));
  const [coveragePct, setCoveragePct] = useState(80);
  const [applyITC, setApplyITC] = useState(true);
  const [result, setResult] = useState<SolarResult | null>(null);

  function handleCalculate() {
    setResult(calcSolar({
      monthlyKwh: parseFloat(monthlyKwh) || 900,
      peakSunHours,
      panelWatts,
      electricityRate: parseFloat(electricityRate) || SOLAR_AVG_ELECTRICITY_RATE_USD_KWH,
      costPerWatt: parseFloat(costPerWatt) || SOLAR_AVG_COST_PER_WATT_INSTALLED,
      coveragePct,
      applyITC,
    }));
  }

  const spec = CALCULATOR_SPECS.solar;

  return (
    <CalculatorShell
      name="Solar Savings Calculator"
      description="Estimate how many solar panels you need and how much you could save on your electricity bill over 25 years."
      icon="solar"
      category="high-value"
      accentColor="#f59e0b"
      hasResult={result !== null}
      onCalculate={handleCalculate}
      disclaimer="Solar estimates depend on shading, roof pitch, local utility rates, and available rebates. Always get 3+ quotes from certified solar installers."
      inputsPanel={
        <div className="space-y-4">
          <InputField id="monthly-kwh" label="Monthly Electricity Usage" unit="kWh" type="number" min="0" step="10" placeholder="900" value={monthlyKwh} onChange={(e) => { setMonthlyKwh(e.target.value); setResult(null); }} hint="Find this on your electricity bill. US avg is ~900 kWh/month" />
          <SelectField id="sun-hours" label="Peak Sun Hours (by region)" value={peakSunHours} onChange={(e) => { setPeakSunHours(parseFloat(e.target.value)); setResult(null); }} options={SUN_HOURS_OPTIONS} hint="Use NREL's PVWatts tool for your exact ZIP code" />
          <SelectField id="panel-watts" label="Panel Wattage" value={panelWatts} onChange={(e) => { setPanelWatts(parseInt(e.target.value)); setResult(null); }} options={PANEL_WATT_OPTIONS} />
          <SelectField id="coverage-pct" label="Bill Offset Goal" value={coveragePct} onChange={(e) => { setCoveragePct(parseInt(e.target.value)); setResult(null); }} options={COVERAGE_OPTIONS} />
          <div className="grid grid-cols-2 gap-3">
            <InputField id="electricity-rate" label="Electricity Rate" unit="$/kWh" type="number" min="0" step="0.01" placeholder="0.16" value={electricityRate} onChange={(e) => { setElectricityRate(e.target.value); setResult(null); }} hint="EIA avg: $0.16/kWh" />
            <InputField id="cost-per-watt" label="Install Cost" unit="$/W" type="number" min="0" step="0.10" placeholder="2.80" value={costPerWatt} onChange={(e) => { setCostPerWatt(e.target.value); setResult(null); }} hint="SEIA avg: $2.80/W" />
          </div>
          <CheckboxField id="apply-itc" label={`Apply ${Math.round(SOLAR_FEDERAL_ITC_PCT * 100)}% Federal Tax Credit (ITC)`} hint="Reduces system cost — verify eligibility with a tax advisor" checked={applyITC} onChange={(v) => { setApplyITC(v); setResult(null); }} />
        </div>
      }
      resultsPanel={
        result && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <ResultCard label="System Size" value={result.systemKw} unit="kW" accent size="md" />
              <ResultCard label="Panels Needed" value={result.numPanels} unit="panels" accent />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ResultCard label="Monthly Savings" value={fmt(result.monthlySavings)} highlight size="md" sub="Est. electricity savings" />
              <ResultCard label="Annual Savings" value={fmt(result.annualSavings)} highlight />
            </div>
            <ResultCard label="Payback Period" value={result.paybackYears} unit="years" accent size="lg" sub={`${result.billCoverageActualPct}% of bill offset`} />
            <div className="pt-4 mt-2">
              <div className="font-mono text-[9px] font-bold uppercase tracking-wider text-[var(--ink-faint)] pb-2 select-none">
                Investment Breakdown
              </div>
              <div className="flex flex-col border-b border-[var(--line)]">
                {[
                  { label: "System gross cost", value: fmt(result.systemGrossCost) },
                  { label: `Federal ITC (${Math.round(SOLAR_FEDERAL_ITC_PCT * 100)}%)`, value: applyITC ? `−${fmt(result.taxCredit)}` : "Not applied" },
                  { label: "Net cost after credit", value: fmt(result.systemNetCost), bold: true },
                  { label: "Est. 25-year net profit", value: result.savings25Years >= 0 ? fmt(result.savings25Years) : `−${fmt(Math.abs(result.savings25Years))}`, green: result.savings25Years > 0 },
                ].map((row, i) => {
                  const isHighlight = row.bold || row.green;
                  return (
                    <div
                      key={i}
                      className={`flex justify-between items-center py-2.5 px-3 border-t border-[var(--line)] transition-all ${
                        isHighlight
                          ? "border-l-3 border-l-[var(--accent)] pl-2 bg-[var(--surface-muted)]/30"
                          : "border-l-3 border-l-transparent"
                      }`}
                    >
                      <span className={`text-[12.5px] font-sans ${isHighlight ? "text-[var(--accent)] font-extrabold" : "text-[var(--ink-soft)] font-semibold"}`}>
                        {row.label}
                      </span>
                      <span className={`font-mono text-[13px] ${isHighlight ? "text-[var(--accent)] font-extrabold" : "text-[var(--ink)] font-bold"}`}>
                        {row.value}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="info-box flex items-start gap-2">
              <Icon name="info" className="w-4 h-4 mt-0.5 shrink-0" />
              <span><strong>Annual production:</strong> ~{result.annualProductionKwh.toLocaleString()} kWh — based on NREL&apos;s US average of 1,300 kWh per installed kW.</span>
            </div>
          </div>
        )
      }
    >
      {/* ── Informational Grid ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center text-[var(--ink)]">
        <div className="space-y-4 md:order-1">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">Panel Selection &amp; System Sizing Guide</h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Choosing the right solar panel for your home requires balancing wattage, efficiency, and roof space. Modern residential panels range from <strong>{spec.buyingGuide.panelWattageRange.value}</strong>, with higher-wattage panels producing more power per square foot of roof. According to the SEIA, the median <Link href="/articles/solar-panel-costs/" className="text-[var(--accent)] hover:underline font-semibold">installed cost</Link> for a residential system under 10 kW is <strong>${spec.buyingGuide.avgCostPerWatt.value}/watt</strong> — fully installed, including hardware, labor, and permitting. A typical US household consuming 900 kWh/month needs a system between <strong>{spec.buyingGuide.systemSizeTypical.value}</strong> to offset 80–100% of its electricity bill, depending on location and shading.
          </p>
        </div>
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-2">
          <Image src="/images/solar_buying_guide.webp" alt="Residential rooftop solar panels installed on a suburban home in the United States" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" priority />
        </div>

        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-3">
          <Image src="/images/solar_site_prep.webp" alt="Solar installer on a roof assessing pitch and shading conditions before panel installation" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" loading="lazy" />
        </div>
        <div className="space-y-4 md:order-4">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">Roof Readiness &amp; Site Assessment</h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Before signing a solar contract, verify that your roof can support a 25-year system. Most installers require a minimum roof pitch of <strong>{spec.sitePrep.minRoofPitch.value}</strong> for proper water runoff and panel efficiency. Critically, your roof must have at least <strong>{spec.sitePrep.minRoofAge.value}</strong> — installing solar on a roof that will need replacement in a few years means paying to remove and reinstall every panel. South-facing roofs at a 30–45° tilt produce the most energy in the continental US, though east- and west-facing orientations are increasingly common with modern microinverter systems. Consider running a professional <Link href="/articles/is-home-energy-audit-worth-it/" className="text-[var(--accent)] hover:underline font-semibold">home energy audit</Link> to optimize your baseline consumption, which impacts your solar <Link href="/articles/solar-panel-payback-period-explained/" className="text-[var(--accent)] hover:underline font-semibold">payback period</Link>.
          </p>
        </div>
      </div>

      {/* ── Citation Block ── */}
      <CitationSection
        standardCode={spec.industryStandards.itcRate2026.standardCode}
        sourceName={spec.industryStandards.itcRate2026.sourceName}
        sourceUrl={spec.industryStandards.itcRate2026.sourceUrl}
        paragraphs={[
          <p key="p1">
            The Inflation Reduction Act (IRA) extended and expanded the residential solar Investment Tax Credit (ITC) to <b>{spec.industryStandards.itcRate2026.value}%</b> for systems installed through 2032. This means a homeowner who installs a $25,000 solar system can claim a <b>$7,500 reduction</b> in their federal income taxes — not a deduction, but a dollar-for-dollar credit. The credit covers panels, inverters, battery storage, labor, permitting, and interconnection fees. After 2032, the credit steps down to 26% in 2033 and 22% in 2034 before expiring for residential installations under current law.
          </p>,
          <p key="p2">
            To qualify, you must own the solar system outright (leased systems do not qualify), it must be installed at your primary or secondary US residence, and you must have sufficient federal tax liability to absorb the credit. Unused credit can be carried forward to future tax years. Always consult a qualified tax professional before claiming the ITC, as eligibility rules and income requirements may apply depending on your tax situation.
          </p>
        ]}
        keyRequirements={[
          "Residential solar installations qualify for a 30% federal tax credit through 2032.",
          "Tax credit covers solar panels, inverters, battery storage, labor, and permits.",
          "The homeowner must own the solar system; leased systems do not qualify.",
          "Unused credit can be carried forward to subsequent federal tax years."
        ]}
      />

      <FaqSection calculatorId="solar" />
      <RelatedCalculators calculatorId="solar" />
    </CalculatorShell>
  );
}
